import { useCallback, useEffect, useRef, useState } from 'react'
import { getCookie } from '../scripts/cookies.script'
import { getConversations } from '../services/chat/chat.service'
import { activeCompanyId } from '../scripts/chat.auth'

export type ChatWsStatus = 'connecting' | 'open' | 'closed'

export interface WsMessageCreated {
  _id: string;
  conversation_id: string;
  sender_user_id: string;
  content: string;
  document_ref: unknown;
  read_by: Array<{ user_id: string; read_at: string }>;
  created_at: string;
}

export interface WsMessageRead {
  conversation_id: string;
  user_id: string;
  read_at: string;
}

interface UseChatWebSocketOptions {
  onMessageCreated: (data: WsMessageCreated) => void;
  onMessageRead: (data: WsMessageRead) => void;
}

const WS_BASE_URL = process.env.REACT_APP_CHAT_WS_URL || 'wss://chat.digitaladmin.io'
const MIN_BACKOFF = 1000
const MAX_BACKOFF = 30000

export function useChatWebSocket({ onMessageCreated, onMessageRead }: UseChatWebSocketOptions) {
  const [status, setStatus] = useState<ChatWsStatus>('closed')
  const wsRef = useRef<WebSocket | null>(null)
  const backoffRef = useRef(MIN_BACKOFF)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)
  const handlersRef = useRef({ onMessageCreated, onMessageRead })

  // Keep handlers ref fresh without re-triggering effects
  handlersRef.current = { onMessageCreated, onMessageRead }

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const connect = useCallback(async () => {
    if (!mountedRef.current) return

    // Check token availability
    const token = getCookie('access')
    if (!token) {
      setStatus('closed')
      return
    }

    // Pre-flight: call a REST endpoint to trigger token refresh if expired
    // (the axios interceptor handles refresh automatically)
    const companyId = activeCompanyId()
    if (companyId) {
      try {
        await getConversations()
      } catch {
        // If REST fails, token is likely invalid — retry later
        if (mountedRef.current) {
          scheduleReconnect()
        }
        return
      }
    }

    // Re-read token after potential refresh
    const freshToken = getCookie('access')
    if (!freshToken || !mountedRef.current) {
      setStatus('closed')
      return
    }

    setStatus('connecting')

    const ws = new WebSocket(`${WS_BASE_URL}?token=${freshToken}`)
    wsRef.current = ws

    ws.onopen = () => {
      if (!mountedRef.current) { ws.close(); return }
      setStatus('open')
      backoffRef.current = MIN_BACKOFF
    }

    ws.onmessage = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        if (envelope.type === 'message.created' && envelope.data) {
          handlersRef.current.onMessageCreated(envelope.data)
        } else if (envelope.type === 'message.read' && envelope.data) {
          handlersRef.current.onMessageRead(envelope.data)
        }
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      wsRef.current = null
      if (mountedRef.current) {
        setStatus('closed')
        scheduleReconnect()
      }
    }

    ws.onerror = () => {
      // onclose will fire after onerror
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const scheduleReconnect = useCallback(() => {
    clearTimer()
    const delay = backoffRef.current
    backoffRef.current = Math.min(delay * 2, MAX_BACKOFF)
    timerRef.current = setTimeout(() => {
      if (mountedRef.current) connect()
    }, delay)
  }, [connect, clearTimer])

  const disconnect = useCallback(() => {
    clearTimer()
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }
    setStatus('closed')
  }, [clearTimer])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    mountedRef.current = true
    connect()
    return () => {
      mountedRef.current = false
      disconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { status, disconnect }
}
